import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@core/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { checkIsAdmin, checkIsAdminAsync } from "../../utils/adminCheck";

interface AdminGuardProps {
  children: React.ReactNode;
}

type AdminCheckState = "checking" | "allowed" | "denied";

export const AdminGuard: React.FC<AdminGuardProps> = ({ children }) => {
  const { t } = useTranslation("forms");
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  const [dbAdminCheck, setDbAdminCheck] = useState<AdminCheckState>("checking");

  // Check sync d'abord (rapide). Si OK on peut court-circuiter le check DB.
  const syncAdmin = checkIsAdmin(user, profile);

  useEffect(() => {
    let cancelled = false;
    if (loading || !user) return;
    if (syncAdmin) {
      setDbAdminCheck("allowed");
      return;
    }
    // Fallback : vérifier dans la table admin_users.
    // Safety net : si la RPC `is_admin` ne résout jamais (réseau/RLS),
    // on fail-closed au bout de 5s pour ne pas bloquer le spinner.
    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        console.warn("[AdminGuard] is_admin check timed out — denying");
        setDbAdminCheck("denied");
      }
    }, 5000);
    checkIsAdminAsync(user)
      .then((ok) => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        setDbAdminCheck(ok ? "allowed" : "denied");
      })
      .catch(() => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        setDbAdminCheck("denied");
      });
    return () => {
      cancelled = true;
    };
  }, [loading, user, syncAdmin]);

  if (loading || (user && !syncAdmin && dbAdminCheck === "checking")) {
    return (
      <div className="min-h-screen-safe bg-[#030712] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-gray-400">{t("admin.guard.checking")}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!syncAdmin && dbAdminCheck !== "allowed") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
